import React, { useState, useEffect } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Switch, FormControlLabel, Typography, TextField,
    IconButton, Tooltip, Chip, Button, Dialog, DialogTitle,
    DialogContent, DialogActions, useTheme, useMediaQuery, Stack, Card, CardContent,
    Avatar, Select, MenuItem, FormControl, InputLabel, CircularProgress, Alert,
    InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import SendIcon from '@mui/icons-material/Send';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import RefreshIcon from '@mui/icons-material/Refresh';
import VerifiedIcon from '@mui/icons-material/Verified';
import { adminApi } from '../../services/api';

const CandidatesManager = () => {
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editValues, setEditValues] = useState({});
    const [reachStats, setReachStats] = useState(null);
    const [reachFilter, setReachFilter] = useState('all');
    const [bccEnabled, setBccEnabled] = useState(() => {
        return localStorage.getItem('candidatesBccRegister') === 'true';
    });
    const [emailDispatchDialog, setEmailDispatchDialog] = useState({
        open: false,
        candidate: null,
        templateId: 1
    });

    const handleBccToggle = (checked) => {
        setBccEnabled(checked);
        localStorage.setItem('candidatesBccRegister', checked ? 'true' : 'false');
    };
    const [locationFilter, setLocationFilter] = useState(() => {
        return localStorage.getItem('candidatesLocationFilter') || 'all';
    });
    const [availableLocations, setAvailableLocations] = useState([]);
    const [statusFilter, setStatusFilter] = useState(() => {
        return localStorage.getItem('candidatesStatusFilter') || 'all';
    });
    const [typeFilter, setTypeFilter] = useState(() => {
        return localStorage.getItem('candidatesTypeFilter') || 'all';
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [blacklistDialog, setBlacklistDialog] = useState({ open: false, candidateId: null, candidateName: '' });
    const [deleteDialog, setDeleteDialog] = useState({ 
        open: false, 
        candidateId: null,
        candidateName: '' 
    });
    const [emailStatuses, setEmailStatuses] = useState({});
    const [sendingEmails, setSendingEmails] = useState({});
    const [approvingCandidates, setApprovingCandidates] = useState({});
    const [cityInfo, setCityInfo] = useState([]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // In-house Scouting Agent state
    const [scoutDialogOpen, setScoutDialogOpen] = useState(false);
    const [scoutCity, setScoutCity] = useState('');
    const [scoutCount, setScoutCount] = useState(10);
    const [scoutLoading, setScoutLoading] = useState(false);
    const [scoutMessage, setScoutMessage] = useState(null);

    const handleRunScout = async () => {
        if (!scoutCity) {
            alert('Please select or enter a target city code (e.g. SIN, LON, NYC)');
            return;
        }
        try {
            setScoutLoading(true);
            setScoutMessage(null);
            const res = await adminApi.runScout(scoutCity, scoutCount);
            if (res.success) {
                setScoutMessage({
                    type: 'success',
                    text: res.message || `Scouted successfully! Added ${res.data?.insertedCount} new candidates.`
                });
                await fetchCandidates();
                await fetchLocations();
            } else {
                throw new Error(res.error || 'Scout failed');
            }
        } catch (err) {
            const errorMsg = err.response?.data?.error 
                || (err.code === 'ECONNABORTED' 
                    ? 'The scouting agent is running in the background. Refresh in a moment to see newly discovered candidates.' 
                    : err.message || 'Scouting request failed');
            setScoutMessage({
                type: 'error',
                text: errorMsg
            });
            // Refresh list in case candidates were discovered and inserted before timeout
            fetchCandidates();
            fetchLocations();
        } finally {
            setScoutLoading(false);
        }
    };

    useEffect(() => {
        fetchCandidates();
        fetchReachStats();
        fetchLocations();
        fetchCityInfo();
    }, []);

    const fetchReachStats = async () => {
        try {
            const stats = await adminApi.getEmailReachStats(locationFilter === 'all' ? null : locationFilter);
            setReachStats(stats);
        } catch (error) {
            console.error('Error fetching reach stats:', error);
        }
    };

    const fetchCandidates = async () => {
        try {
            const response = await adminApi.getCandidates(locationFilter === 'all' ? null : locationFilter);
            setCandidates(response.data?.data || []);
            
            const initialStatuses = {};
            response.data?.data.forEach(candidate => {
                if (candidate.latest_email_event) {
                    initialStatuses[candidate.id] = {
                        latestStatus: {
                            currentStatus: candidate.latest_email_event,
                            lastEventType: candidate.latest_email_event,
                            errorMessage: candidate.email_error_message
                        }
                    };
                }
            });
            setEmailStatuses(initialStatuses);
        } catch (error) {
            console.error('Error fetching candidates:', error);
            setCandidates([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchLocations = async () => {
        try {
            const response = await adminApi.getCandidateLocations();
            if (response.data?.success) {
                setAvailableLocations(response.data.data.map(loc => loc.city_code));
            }
        } catch (error) {
            console.error('Error fetching locations:', error);
            setAvailableLocations([]);
        }
    };

    const fetchCityInfo = async () => {
        try {
            const response = await adminApi.getCityInfo();
            setCityInfo(response.data || []);
        } catch (error) {
            console.error('Error fetching city info:', error);
            setCityInfo([]);
        }
    };

    const handleApprove = async (id) => {
        try {
            setApprovingCandidates(prev => ({ ...prev, [id]: true }));
            const result = await adminApi.approveCandidate(id);
            
            if (result.success) {
                // Optimistically transition candidate to approved status
                setCandidates(prev => prev.map(c => {
                    if (c.id === id) {
                        return {
                            ...c,
                            is_approved: true,
                            status: 'approved',
                            approved_at: new Date().toISOString()
                        };
                    }
                    return c;
                }));
                // Silently refresh candidates and reach stats in background without full screen unmounting
                fetchCandidates();
                fetchReachStats();
            } else {
                throw new Error(result.error || 'Failed to approve candidate');
            }
        } catch (error) {
            console.error('Error approving candidate:', error);
            alert(error.response?.data?.error || error.message || 'Failed to approve candidate');
        } finally {
            setApprovingCandidates(prev => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
        }
    };

    const handleBlacklist = async () => {
        try {
            const res = await adminApi.blacklistCandidate(
                blacklistDialog.candidateId, 
                'Manual admin blacklist'
            );
            if (res.data?.success === false) {
                throw new Error(res.data.error || 'Failed to blacklist candidate');
            }
            await fetchCandidates();
            await fetchReachStats();
            setBlacklistDialog({ open: false, candidateId: null, candidateName: '' });
            setEditValues({});
        } catch (error) {
            console.error('Error blacklisting candidate:', error);
            alert(error.response?.data?.error || error.message || 'Failed to blacklist candidate');
        }
    };

    const handleDelete = async () => {
        try {
            await adminApi.deleteCandidate(deleteDialog.candidateId);
            await fetchCandidates();
            setDeleteDialog({ open: false, candidateId: null, candidateName: '' });
        } catch (error) {
            console.error('Error deleting candidate:', error);
            alert('Failed to delete candidate');
        }
    };

    const handleOpenEmailDialog = (candidate) => {
        let defaultTemplate = adminApi.EMAIL_TEMPLATES.WELCOME;
        const seq = candidate.email_sequence || 0;
        if (candidate.is_agency) {
            defaultTemplate = seq === 0 ? adminApi.EMAIL_TEMPLATES.AGENCY_PITCH : (seq === 1 ? adminApi.EMAIL_TEMPLATES.REMINDER_AGENCY : adminApi.EMAIL_TEMPLATES.FINAL_CHECK_AGENCY);
        } else {
            defaultTemplate = seq === 0 ? adminApi.EMAIL_TEMPLATES.WELCOME : (seq === 1 ? adminApi.EMAIL_TEMPLATES.REMINDER : adminApi.EMAIL_TEMPLATES.FINAL_CHECK);
        }
        setEmailDispatchDialog({
            open: true,
            candidate,
            templateId: defaultTemplate
        });
    };

    const handleConfirmSendEmail = async () => {
        const candidate = emailDispatchDialog.candidate;
        const templateId = emailDispatchDialog.templateId;
        if (!candidate) return;
        const id = candidate.id;

        try {
            setSendingEmails(prev => ({ ...prev, [id]: true }));
            setEmailDispatchDialog(prev => ({ ...prev, open: false }));

            const result = await adminApi.sendCodeOwnerEmail(id, templateId, { bcc: bccEnabled });
            if (!result.success) {
                throw new Error(result.error || 'Failed to send email');
            }

            await fetchCandidates();
            await fetchReachStats();
        } catch (error) {
            console.error('Error sending email:', error);
            alert(error.message || 'Failed to send email');
            await fetchCandidates();
            await fetchReachStats();
        } finally {
            setSendingEmails(prev => {
                const newState = { ...prev };
                delete newState[id];
                return newState;
            });
        }
    };

    const handleSendCodeOwnerEmail = async (id, templateId = adminApi.EMAIL_TEMPLATES.WELCOME) => {
        try {
            setSendingEmails(prev => ({ ...prev, [id]: true }));
            
            const result = await adminApi.sendCodeOwnerEmail(id, templateId, { bcc: bccEnabled });
            if (!result.success) {
                throw new Error(result.error || 'Failed to send email');
            }
            await fetchCandidates();
            await fetchReachStats();
        } catch (error) {
            console.error('Error sending welcome email:', error);
            alert(error.message || 'Failed to send email');
        } finally {
            setSendingEmails(prev => {
                const newState = { ...prev };
                delete newState[id];
                return newState;
            });
        }
    };

    const handleToggleAgency = async (candidateId, currentIsAgency) => {
        const nextValue = !currentIsAgency;
        try {
            setCandidates(prev => prev.map(c => c.id === candidateId ? { ...c, is_agency: nextValue } : c));
            await adminApi.updateCandidateAgency(candidateId, nextValue);
        } catch (error) {
            console.error('Error toggling agency status:', error);
            setCandidates(prev => prev.map(c => c.id === candidateId ? { ...c, is_agency: currentIsAgency } : c));
            alert('Failed to update agency status');
        }
    };

    const getStatusChip = (candidate) => {
        if (candidate.is_blacklisted) {
            return <Chip label="Blacklisted" color="error" size="small" />;
        }
        return <Chip 
            label={candidate.status} 
            color={
                candidate.status === "Emailed" ? "warning" :
                candidate.is_approved ? "success" : 
                "primary"
            } 
            size="small" 
        />;
    };

    const handleUrlClick = (url) => {
        // Ensure URL has proper protocol
        const properUrl = url.startsWith('http') ? url : `https://${url}`;
        // Open in new tab with security best practices
        window.open(properUrl, '_blank', 'noopener,noreferrer');
    };

    const shouldShowEmailIcon = (candidate) => {
        // Only show email icon if:
        // 1. Candidate is approved
        // 2. Has valid email
        // 3. No email has been sent yet (no email event)
        // 4. OR email event is 'new'
        // 5. OR email event is not in terminal state (delivered, open or failed)
        const status = emailStatuses[candidate.id]?.latestStatus?.currentStatus;
        return candidate.is_approved && 
               candidate.company_email && 
               (!status || status === 'new' || (status !== 'delivered' && status !== 'open' && status !== 'failed'));
    };

    const shouldShowActionIcons = (candidate) => {
        const isApproving = Boolean(approvingCandidates[candidate.id]);
        const isSending = sendingEmails[candidate.id];
        const reachStatus = candidate.email_reach_status || 'unsent';
        const sequence = candidate.email_sequence || 0;
        const isRegistered = candidate.registration_completed;

        if (isApproving) {
            return {
                approve: false,
                approving: true,
                email: false,
                blacklist: false,
                delete: false,
                progress: false,
                bounced: false
            };
        }

        if (isSending || reachStatus === 'pending') {
            return {
                approve: false,
                approving: false,
                email: false,
                blacklist: false,
                delete: false,
                progress: true,
                bounced: false
            };
        }

        if (candidate.is_blacklisted) {
            return {
                approve: false,
                approving: false,
                email: false,
                blacklist: false,
                delete: true,
                progress: false,
                bounced: false
            };
        }

        if (!candidate.is_approved && !candidate.is_blacklisted) {
            return {
                approve: true,
                approving: false,
                email: false,
                blacklist: true,
                delete: true,
                progress: false,
                bounced: false
            };
        }

        if (isRegistered) {
            return {
                approve: false,
                approving: false,
                email: false,
                blacklist: true,
                delete: true,
                progress: false,
                bounced: false
            };
        }

        if (reachStatus === 'bounced_invalid') {
            return {
                approve: false,
                approving: false,
                email: false,
                blacklist: true,
                delete: true,
                progress: false,
                bounced: true
            };
        }

        if (candidate.is_approved && candidate.company_email) {
            return {
                approve: false,
                approving: false,
                email: sequence < 5,
                blacklist: true,
                delete: true,
                progress: false,
                bounced: false
            };
        }

        return {
            approve: false,
            approving: false,
            email: false,
            blacklist: true,
            delete: true,
            progress: false,
            bounced: false
        };
    };

    const getEmailActionTooltip = (candidate) => {
        const seq = candidate.email_sequence || 0;
        const reachStatus = candidate.email_reach_status || 'unsent';
        if (seq === 0) {
            return "Send Initial Welcome Invitation (Sequence #1) from konstantin.ryabov@mytowncode.com";
        }
        const signal = reachStatus === 'opened' ? 'Opened' : (reachStatus === 'clicked' ? 'Clicked' : 'Verified Real');
        return `${signal}! Mailbox confirmed active. Send Reminder #${seq} (Touch ${seq + 1}/5)`;
    };

    const renderEmailReachChip = (candidate) => {
        const reachStatus = candidate.email_reach_status || 'unsent';
        const sequence = candidate.email_sequence || 0;

        if (candidate.registration_completed) {
            return (
                <Chip 
                    label="Registered" 
                    size="small" 
                    sx={{ bgcolor: '#DCFCE7', color: '#166534', fontWeight: 700, fontSize: '0.68rem', height: 22 }} 
                />
            );
        }

        switch (reachStatus) {
            case 'pending':
                return (
                    <Tooltip title="Email in transit, awaiting delivery receipt from mail server">
                        <Chip 
                            icon={<PendingIcon sx={{ '&&': { fontSize: 13, color: '#B45309' } }} />}
                            label={`In Transit (Seq ${sequence})`}
                            size="small"
                            sx={{ bgcolor: '#FEF3C7', color: '#B45309', fontWeight: 600, fontSize: '0.68rem', height: 22 }}
                        />
                    </Tooltip>
                );
            case 'verified_real':
                return (
                    <Tooltip title={`Mailbox confirmed real & active (Delivered). Ready for follow-up! ${candidate.last_email_sent_at ? `Sent ${new Date(candidate.last_email_sent_at).toLocaleDateString()}` : ''}`}>
                        <Chip 
                            icon={<CheckCircleIcon sx={{ '&&': { fontSize: 13, color: '#15803D' } }} />}
                            label={`Verified Real (Seq ${sequence})`}
                            size="small"
                            sx={{ bgcolor: '#DCFCE7', color: '#15803D', fontWeight: 600, fontSize: '0.68rem', height: 22 }}
                        />
                    </Tooltip>
                );
            case 'opened':
                return (
                    <Tooltip title={`Recipient opened email! High engagement signal. ${candidate.last_email_sent_at ? `Sent ${new Date(candidate.last_email_sent_at).toLocaleDateString()}` : ''}`}>
                        <Chip 
                            icon={<MarkEmailReadIcon sx={{ '&&': { fontSize: 13, color: '#7E22CE' } }} />}
                            label={`Opened (Seq ${sequence})`}
                            size="small"
                            sx={{ bgcolor: '#F3E8FF', color: '#7E22CE', fontWeight: 700, fontSize: '0.68rem', height: 22 }}
                        />
                    </Tooltip>
                );
            case 'clicked':
                return (
                    <Tooltip title={`Recipient clicked email link! Peak interest. ${candidate.last_email_sent_at ? `Sent ${new Date(candidate.last_email_sent_at).toLocaleDateString()}` : ''}`}>
                        <Chip 
                            icon={<TouchAppIcon sx={{ '&&': { fontSize: 13, color: '#1D4ED8' } }} />}
                            label={`Clicked (Seq ${sequence})`}
                            size="small"
                            sx={{ bgcolor: '#DBEAFE', color: '#1D4ED8', fontWeight: 700, fontSize: '0.68rem', height: 22 }}
                        />
                    </Tooltip>
                );
            case 'bounced_invalid':
                return (
                    <Tooltip title={`Bounced / Invalid address: ${candidate.last_email_error || 'Delivery rejected'}. Do not email.`}>
                        <Chip 
                            icon={<ErrorIcon sx={{ '&&': { fontSize: 13, color: '#B91C1C' } }} />}
                            label="Bounced"
                            size="small"
                            sx={{ bgcolor: '#FEE2E2', color: '#B91C1C', fontWeight: 600, fontSize: '0.68rem', height: 22 }}
                        />
                    </Tooltip>
                );
            case 'unsent':
            default:
                return (
                    <Chip 
                        label="Unsent" 
                        size="small" 
                        variant="outlined" 
                        sx={{ color: '#64748B', borderColor: '#CBD5E1', fontSize: '0.68rem', height: 22 }} 
                    />
                );
        }
    };

    const formatDdMmmYy = (dateStr) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return null;
        const day = String(d.getDate()).padStart(2, '0');
        const month = d.toLocaleString('en-US', { month: 'short' });
        const year = String(d.getFullYear()).slice(-2);
        return `${day}-${month}-${year}`; // e.g. "15-Oct-26"
    };

    const renderEmailDates = (candidate) => {
        const events = [];
        const history = Array.isArray(candidate.email_history) ? candidate.email_history : [];

        // Initial email (Sequence 1)
        const initSent = history.find(h => h.sequence === 1)?.sent_at 
            || candidate.initial_email_sent_at 
            || (candidate.email_sequence === 1 ? candidate.last_email_sent_at : null);
        if (initSent) {
            events.push({ label: 'Initial', date: initSent, seq: 1 });
        }

        // Reminder 1 (Sequence 2)
        const rem1Sent = history.find(h => h.sequence === 2)?.sent_at 
            || candidate.reminder_1_sent_at 
            || (candidate.email_sequence === 2 ? candidate.last_email_sent_at : null);
        if (rem1Sent) {
            events.push({ label: 'Rem #1', date: rem1Sent, seq: 2 });
        }

        // Reminder 2 (Sequence 3)
        const rem2Sent = history.find(h => h.sequence === 3)?.sent_at 
            || candidate.reminder_2_sent_at 
            || (candidate.email_sequence === 3 ? candidate.last_email_sent_at : null);
        if (rem2Sent) {
            events.push({ label: 'Rem #2', date: rem2Sent, seq: 3 });
        }

        // Reminder 3 (Sequence 4)
        const rem3Sent = history.find(h => h.sequence === 4)?.sent_at 
            || candidate.reminder_3_sent_at 
            || (candidate.email_sequence === 4 ? candidate.last_email_sent_at : null);
        if (rem3Sent) {
            events.push({ label: 'Rem #3', date: rem3Sent, seq: 4 });
        }

        // Reminder 4 (Sequence 5)
        const rem4Sent = history.find(h => h.sequence === 5)?.sent_at 
            || candidate.reminder_4_sent_at 
            || (candidate.email_sequence === 5 ? candidate.last_email_sent_at : null);
        if (rem4Sent) {
            events.push({ label: 'Rem #4', date: rem4Sent, seq: 5 });
        }

        // Fallback: If no event in list yet candidate has last_email_sent_at
        if (events.length === 0 && candidate.last_email_sent_at) {
            const seq = candidate.email_sequence || 1;
            events.push({
                label: seq === 1 ? 'Initial' : `Rem #${seq - 1}`,
                date: candidate.last_email_sent_at,
                seq
            });
        }

        if (events.length === 0) return null;

        return (
            <Box sx={{ mt: 0.35, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                {events.map((ev, idx) => {
                    const formatted = formatDdMmmYy(ev.date);
                    if (!formatted) return null;
                    const isInit = ev.seq === 1;
                    const labelColor = isInit ? '#64748B' : (ev.seq === 2 ? '#7C3AED' : '#D97706');
                    const valueColor = isInit ? '#0F172A' : (ev.seq === 2 ? '#5B21B6' : '#92400E');
                    return (
                        <Typography key={idx} variant="caption" sx={{ fontSize: '0.67rem', lineHeight: 1.15, display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <span style={{ color: labelColor, fontWeight: 600 }}>{ev.label}:</span>
                            <span style={{ fontWeight: 700, color: valueColor }}>{formatted}</span>
                        </Typography>
                    );
                })}
            </Box>
        );
    };

    const getEmailStatusIcon = (candidateId) => {
        const candidate = candidates.find(c => c.id === candidateId);
        if (!candidate) return null;
        const reachStatus = candidate.email_reach_status || 'unsent';
        const isSending = sendingEmails[candidateId];

        if (isSending || reachStatus === 'pending') {
            return (
                <Tooltip title="Email in transit...">
                    <IconButton size="small" disabled>
                        <PendingIcon color="warning" />
                    </IconButton>
                </Tooltip>
            );
        }
        if (reachStatus === 'bounced_invalid') {
            return (
                <Tooltip title={`Email delivery bounced: ${candidate.last_email_error || 'Invalid address'}`}>
                    <IconButton size="small" disabled>
                        <ErrorIcon color="error" />
                    </IconButton>
                </Tooltip>
            );
        }
        return null;
    };

    const getEmailStatusTooltip = (candidateId) => {
        const candidate = candidates.find(c => c.id === candidateId);
        if (!candidate) return '';
        return getEmailActionTooltip(candidate);
    };

    // Add helper function to get notes content
    const getNotesContent = (candidate) => {
        if (candidate.email_reach_status === 'bounced_invalid') {
            return candidate.last_email_error || 'Email bounced (undeliverable)';
        }
        return candidate.admin_notes || '';
    };

    const renderMobileView = () => (
        <Stack spacing={0.5}>
            {Array.isArray(getFilteredCandidates()) && getFilteredCandidates().map((candidate) => (
                <Card 
                    key={candidate.id}
                    variant="outlined"
                    sx={{ 
                        borderColor: candidate.registration_completed ? '#86EFAC' : '#E2E8F0',
                        bgcolor: candidate.registration_completed ? '#F0FDF4' : '#FFFFFF',
                        borderLeft: candidate.registration_completed ? '4px solid #16A34A' : undefined,
                        '& .MuiCardContent-root': { 
                            p: 0.75,
                            '&:last-child': { pb: 0.75 }
                        }
                    }}
                >
                    <CardContent>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'stretch',
                            gap: 1
                        }}>
                            {(() => {
                                const logoSrc = candidate.code_image_url || 
                                    (candidate.company_website ? `https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${encodeURIComponent(candidate.company_website)}&size=128` : null);
                                return (
                                    <Box sx={{ 
                                        width: 48, 
                                        height: 48, 
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        flexDirection: 'column',
                                        gap: 0.5
                                    }}>
                                        <Avatar
                                            src={logoSrc}
                                            alt={`${candidate.company_name} logo`}
                                            variant="rounded"
                                            sx={{ 
                                                width: 44, 
                                                height: 44,
                                                bgcolor: '#F1F5F9',
                                                border: '1px solid #E2E8F0',
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                fontSize: '0.85rem',
                                                fontWeight: 700,
                                                color: '#475569'
                                            }}
                                        >
                                            {candidate.company_name ? candidate.company_name.charAt(0).toUpperCase() : '🏢'}
                                        </Avatar>
                                        {candidate.earliest_code_value && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontSize: '0.65rem',
                                                    textAlign: 'center',
                                                    width: '100%',
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                {candidate.earliest_code_value}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })()}
                            <Box sx={{ 
                                flexGrow: 1, 
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <Box sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 0.5,
                                    minWidth: 0,
                                    flexWrap: 'nowrap'
                                }}>
                                    <Typography 
                                        variant="caption" 
                                        color="textSecondary"
                                        sx={{ 
                                            fontSize: '0.7rem',
                                            flexShrink: 0
                                        }}
                                    >
                                        {formatDdMmmYy(candidate.created) || new Date(candidate.created).toLocaleDateString()}
                                    </Typography>
                                    <Box sx={{ flexShrink: 0, display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                        {getStatusChip(candidate)}
                                        {renderEmailReachChip(candidate)}
                                    </Box>
                                    {renderEmailDates(candidate)}
                                    {getNotesContent(candidate) && (
                                        <Typography 
                                            variant="caption"
                                            noWrap
                                            sx={{ 
                                                fontSize: '0.7rem',
                                                flexShrink: 1,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                color: theme => {
                                                    const content = getNotesContent(candidate);
                                                    if (content === 'invalid_email' || content.includes('failed')) {
                                                        return theme.palette.error.main;
                                                    }
                                                    return theme.palette.text.secondary;
                                                }
                                            }}
                                        >
                                            {getNotesContent(candidate)}
                                        </Typography>
                                    )}
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography 
                                        variant="subtitle2" 
                                        sx={{ 
                                            fontWeight: 500,
                                            fontSize: '0.8rem',
                                            lineHeight: 1.2,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {candidate.company_name}
                                    </Typography>
                                    {candidate.registration_completed && (
                                        <Tooltip title="Registered Code Owner (Completed Onboarding)">
                                            <CheckCircleIcon sx={{ fontSize: 14, color: '#16A34A', flexShrink: 0 }} />
                                        </Tooltip>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                                    <Tooltip title={candidate.is_agency ? "Marked as Ad / Media Agency (will receive agency pitch). Click to change." : "Marked as Regular Business. Click to mark as Agency."}>
                                        <Chip 
                                            label={candidate.is_agency ? "Agency" : "Business"} 
                                            size="small" 
                                            color={candidate.is_agency ? "secondary" : "default"}
                                            variant={candidate.is_agency ? "filled" : "outlined"}
                                            onClick={() => handleToggleAgency(candidate.id, candidate.is_agency)}
                                            sx={{ 
                                                height: 18, 
                                                fontSize: '0.62rem', 
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                '&:hover': { opacity: 0.85 }
                                            }} 
                                        />
                                    </Tooltip>
                                    {candidate.industry && (
                                        <Typography 
                                            variant="caption" 
                                            color="textSecondary"
                                            sx={{ 
                                                fontSize: '0.68rem',
                                                lineHeight: 1.2,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {candidate.industry}
                                        </Typography>
                                    )}
                                </Box>

                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: 0.25,
                                    fontSize: '0.7rem',
                                    lineHeight: 1.2,
                                    mt: 0.5
                                }}>
                                    <Typography 
                                        variant="caption" 
                                        color="primary"
                                        onClick={() => handleUrlClick(candidate.company_website)}
                                        sx={{ 
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            fontSize: '0.75rem',
                                            lineHeight: 1.2,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            '&:hover': {
                                                color: 'primary.dark'
                                            }
                                        }}
                                    >
                                        {candidate.company_website}
                                    </Typography>
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            lineHeight: 1.2,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {candidate.company_email} • {candidate.city_code}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexShrink: 0,
                                ml: 1,
                                '& .MuiIconButton-root': {
                                    width: 36,
                                    height: 36,
                                    '& .MuiSvgIcon-root': {
                                        fontSize: '1.25rem'
                                    }
                                }
                            }}>
                                <Box>
                                    {shouldShowActionIcons(candidate).approving && (
                                        <Tooltip title="Approving candidate...">
                                            <IconButton size="small" disabled sx={{ width: 36, height: 36 }}>
                                                <CircularProgress size={18} color="primary" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {shouldShowActionIcons(candidate).approve && (
                                        <Tooltip title="Approve">
                                            <IconButton 
                                                onClick={() => handleApprove(candidate.id)}
                                                disabled={loading || Boolean(approvingCandidates[candidate.id])}
                                                color="primary"
                                                size="small"
                                            >
                                                <CheckCircleIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {shouldShowActionIcons(candidate).email && (
                                        <Tooltip title={getEmailStatusTooltip(candidate.id)}>
                                            <IconButton 
                                                onClick={() => handleOpenEmailDialog(candidate)}
                                                disabled={sendingEmails[candidate.id] || loading}
                                                color={candidate.email_sequence > 0 ? "secondary" : "primary"}
                                                size="small"
                                            >
                                                {candidate.email_sequence > 0 ? <ForwardToInboxIcon /> : <EmailIcon />}
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {shouldShowActionIcons(candidate).progress && getEmailStatusIcon(candidate.id)}
                                    {shouldShowActionIcons(candidate).bounced && getEmailStatusIcon(candidate.id)}
                                    {!shouldShowActionIcons(candidate).approving &&
                                     !shouldShowActionIcons(candidate).approve && 
                                     !shouldShowActionIcons(candidate).email && 
                                     !shouldShowActionIcons(candidate).progress && 
                                     !shouldShowActionIcons(candidate).bounced && (
                                        <Box sx={{ width: 36, height: 36 }} />
                                    )}
                                </Box>

                                <Box>
                                    {shouldShowActionIcons(candidate).blacklist && (
                                        <Tooltip title="Blacklist">
                                            <IconButton 
                                                onClick={() => setBlacklistDialog({
                                                    open: true,
                                                    candidateId: candidate.id,
                                                    candidateName: candidate.company_name
                                                })}
                                                color="error"
                                                size="small"
                                            >
                                                <BlockIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>

                                <Box>
                                    {shouldShowActionIcons(candidate).delete && (
                                        <Tooltip title="Delete">
                                            <IconButton 
                                                onClick={() => setDeleteDialog({
                                                    open: true,
                                                    candidateId: candidate.id,
                                                    candidateName: candidate.company_name
                                                })}
                                                color="error"
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            ))}
        </Stack>
    );

    // Modify getUniqueLocations to use availableLocations instead of candidates
    const getUniqueLocations = () => {
        // Get unique location codes that are actually used in candidates
        const uniqueLocations = ['all', ...availableLocations];

        // Create formatted location options
        return uniqueLocations
            .map(loc => {
                if (loc === 'all') {
                    return {
                        value: 'all',
                        label: 'All Locations',
                        sortKey: '0' // Ensure 'All Locations' appears first
                    };
                }

                // Find matching city info
                const cityData = cityInfo.find(ci => 
                    // Match either code_2 or code_3 with the actual location code
                    ci.code_2 === loc || ci.code_3 === loc
                );

                if (cityData) {
                    return {
                        value: loc,
                        label: `${cityData.country_name} / ${cityData.city_name} / ${loc}`,
                        sortKey: `${cityData.country_name}-${cityData.city_name}`
                    };
                }

                // If no city info found, just show the code as is
                return {
                    value: loc,
                    label: loc,
                    sortKey: `Z-${loc}` // Put unknown locations at the end
                };
            })
            .sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    };

    // Add function to get unique statuses
    const getUniqueStatuses = () => {
        if (!Array.isArray(candidates)) return [];
        const statuses = candidates.map(c => c.status).filter(Boolean);
        return ['all', ...new Set(statuses)].map(status => ({
            value: status,
            label: status === 'all' ? 'All Statuses' : status
        }));
    };

    // Update the handler for status filter changes
    const handleStatusFilterChange = (event) => {
        const newValue = event.target.value;
        setStatusFilter(newValue);
        localStorage.setItem('candidatesStatusFilter', newValue);
    };

    // Update the filtered candidates function to include status & reach filter
    const getFilteredCandidates = () => {
        if (!Array.isArray(candidates)) return [];
        return candidates.filter(c => {
            if (statusFilter !== 'all' && c.status !== statusFilter) {
                return false;
            }

            if (typeFilter === 'agency' && !c.is_agency) {
                return false;
            }
            if (typeFilter === 'business' && c.is_agency) {
                return false;
            }

            if (searchQuery && searchQuery.trim()) {
                const q = searchQuery.toLowerCase().trim();
                const matchName = (c.company_name || '').toLowerCase().includes(q);
                const matchIndustry = (c.industry || '').toLowerCase().includes(q);
                const matchProduct = (c.product_name || '').toLowerCase().includes(q);
                const matchCode = (c.code_value || '').toLowerCase().includes(q);
                const matchWebsite = (c.company_website || '').toLowerCase().includes(q);
                const matchNotes = (c.admin_notes || '').toLowerCase().includes(q);
                if (!matchName && !matchIndustry && !matchProduct && !matchCode && !matchWebsite && !matchNotes) {
                    return false;
                }
            }

            if (reachFilter === 'unsent') {
                return (!c.email_reach_status || c.email_reach_status === 'unsent') && !c.registration_completed;
            }
            if (reachFilter === 'in_transit') {
                return c.email_reach_status === 'pending';
            }
            if (reachFilter === 'verified_real') {
                return ['verified_real', 'opened', 'clicked'].includes(c.email_reach_status);
            }
            if (reachFilter === 'opened_clicked') {
                return ['opened', 'clicked'].includes(c.email_reach_status);
            }
            if (reachFilter === 'bounced') {
                return c.email_reach_status === 'bounced_invalid';
            }
            if (reachFilter === 'registered') {
                return c.registration_completed === true;
            }
            if (reachFilter === 'invite_ready') {
                return c.is_approved && !c.registration_completed && (c.email_sequence || 0) === 0;
            }
            if (reachFilter === 'followup_1_ready') {
                if (!c.is_approved || c.registration_completed || !['verified_real', 'opened', 'clicked'].includes(c.email_reach_status) || (c.email_sequence || 0) !== 1) {
                    return false;
                }
                const sentTime = c.last_email_sent_at || c.initial_email_sent_at;
                if (!sentTime) return false;
                const hoursSince = (Date.now() - new Date(sentTime).getTime()) / (1000 * 60 * 60);
                return hoursSince >= 168; // 1 week (7 days)
            }
            if (reachFilter === 'followup_2_ready') {
                if (!c.is_approved || c.registration_completed || !['verified_real', 'opened', 'clicked'].includes(c.email_reach_status) || (c.email_sequence || 0) !== 2) {
                    return false;
                }
                const sentTime = c.last_email_sent_at || c.reminder_1_sent_at;
                if (!sentTime) return false;
                const hoursSince = (Date.now() - new Date(sentTime).getTime()) / (1000 * 60 * 60);
                return hoursSince >= 168; // 1 week (7 days)
            }
            if (reachFilter === 'followup_3_ready') {
                if (!c.is_approved || c.registration_completed || !['verified_real', 'opened', 'clicked'].includes(c.email_reach_status) || (c.email_sequence || 0) !== 3) {
                    return false;
                }
                const sentTime = c.last_email_sent_at || c.reminder_2_sent_at;
                if (!sentTime) return false;
                const hoursSince = (Date.now() - new Date(sentTime).getTime()) / (1000 * 60 * 60);
                return hoursSince >= 720; // 1 month (30 days)
            }
            if (reachFilter === 'followup_4_ready') {
                if (!c.is_approved || c.registration_completed || !['verified_real', 'opened', 'clicked'].includes(c.email_reach_status) || (c.email_sequence || 0) !== 4) {
                    return false;
                }
                const sentTime = c.last_email_sent_at || c.reminder_3_sent_at;
                if (!sentTime) return false;
                const hoursSince = (Date.now() - new Date(sentTime).getTime()) / (1000 * 60 * 60);
                return hoursSince >= 720; // 1 month (30 days)
            }

            return true;
        });
    };

    // Add handler for location filter changes
    const handleLocationFilterChange = (event) => {
        const newValue = event.target.value;
        setLocationFilter(newValue);
        localStorage.setItem('candidatesLocationFilter', newValue);
    };

    // Add useEffect to refetch when locationFilter changes
    useEffect(() => {
        fetchCandidates();
        fetchReachStats();
    }, [locationFilter]);

    if (loading) return <Typography>Loading...</Typography>;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 4 }}>
            {/* Live Reach Statistics & Email Verification Pipeline Header Bar */}
            <Paper
                elevation={0}
                sx={{
                    p: isMobile ? 1.25 : 2,
                    bgcolor: '#FFFFFF',
                    borderRadius: isMobile ? '12px' : '14px',
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                }}
            >
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: isMobile ? 'flex-start' : 'center', 
                    justifyContent: 'space-between', 
                    mb: isMobile ? 1 : 1.5, 
                    flexWrap: 'wrap', 
                    gap: isMobile ? 0.75 : 1 
                }}>
                    {isMobile ? (
                        <>
                            {/* Mobile Row 1: Title + Compact Refresh Button */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
                                    <VerifiedIcon sx={{ color: '#4F46E5', fontSize: 19, flexShrink: 0 }} />
                                    <Typography 
                                        variant="subtitle2" 
                                        sx={{ 
                                            fontWeight: 800, 
                                            color: '#0F172A', 
                                            fontSize: '0.86rem', 
                                            lineHeight: 1.25 
                                        }}
                                    >
                                        Email Reach & Verification Pipeline
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => { fetchCandidates(); fetchReachStats(); }}
                                    sx={{ 
                                        textTransform: 'none', 
                                        borderRadius: '8px', 
                                        fontSize: '0.7rem', 
                                        py: 0.25, 
                                        px: 0.9, 
                                        minWidth: 'auto', 
                                        flexShrink: 0, 
                                        height: 24 
                                    }}
                                >
                                    Refresh
                                </Button>
                            </Box>

                            {/* Mobile Row 2: Sender Chip + BCC Toggle */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', width: '100%' }}>
                                <Chip
                                    label="From: konstantin.ryabov@mytowncode.com"
                                    size="small"
                                    variant="outlined"
                                    sx={{ 
                                        borderColor: '#CBD5E1', 
                                        color: '#475569', 
                                        fontWeight: 600, 
                                        fontSize: '0.67rem', 
                                        height: 22,
                                        maxWidth: '100%'
                                    }}
                                />
                                <Tooltip title="Optional test mode: When ON, sends a blind copy of every outreach email to register@mytowncode.com">
                                    <Box
                                        onClick={() => handleBccToggle(!bccEnabled)}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            px: 0.85,
                                            py: 0.15,
                                            borderRadius: '8px',
                                            bgcolor: bccEnabled ? '#F5F3FF' : '#F8FAFC',
                                            border: '1px solid',
                                            borderColor: bccEnabled ? '#DDD6FE' : '#E2E8F0',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            transition: 'all 0.2s ease',
                                            height: 22
                                        }}
                                    >
                                        <Switch
                                            size="small"
                                            checked={bccEnabled}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                handleBccToggle(e.target.checked);
                                            }}
                                            color="secondary"
                                            sx={{
                                                width: 28,
                                                height: 16,
                                                p: 0,
                                                '& .MuiSwitch-switchBase': {
                                                    p: 0,
                                                    m: '2px',
                                                    '&.Mui-checked': {
                                                        transform: 'translateX(12px)',
                                                        color: '#fff',
                                                        '& + .MuiSwitch-track': {
                                                            backgroundColor: '#7C3AED',
                                                            opacity: 1,
                                                        },
                                                    },
                                                },
                                                '& .MuiSwitch-thumb': {
                                                    width: 12,
                                                    height: 12,
                                                },
                                                '& .MuiSwitch-track': {
                                                    borderRadius: 8,
                                                    backgroundColor: '#CBD5E1',
                                                    opacity: 1,
                                                },
                                            }}
                                        />
                                        <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, color: bccEnabled ? '#7C3AED' : '#64748B', whiteSpace: 'nowrap' }}>
                                            BCC register
                                        </Typography>
                                        <Chip
                                            label={bccEnabled ? "ON" : "OFF"}
                                            size="small"
                                            color={bccEnabled ? "secondary" : "default"}
                                            variant={bccEnabled ? "filled" : "outlined"}
                                            sx={{ height: 16, fontSize: '0.58rem', fontWeight: 800, px: 0.2, minWidth: 26 }}
                                        />
                                    </Box>
                                </Tooltip>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <VerifiedIcon sx={{ color: '#4F46E5', fontSize: 22 }} />
                                    Email Reach & Verification Pipeline
                                </Typography>
                                <Chip
                                    label="From: konstantin.ryabov@mytowncode.com"
                                    size="small"
                                    variant="outlined"
                                    sx={{ borderColor: '#CBD5E1', color: '#475569', fontWeight: 600, fontSize: '0.72rem', height: 22 }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => { fetchCandidates(); fetchReachStats(); }}
                                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '0.75rem', py: 0.4 }}
                                >
                                    Refresh
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>

                {/* Interactive Metric Filter Cards Grid (Replaces old cards & pipeline filter sub-line) */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { 
                        xs: 'repeat(2, 1fr)', 
                        sm: 'repeat(4, 1fr)', 
                        md: 'repeat(4, 1fr)', 
                        lg: 'repeat(8, 1fr)' 
                    },
                    gap: isMobile ? 0.85 : 1.15,
                    mb: 0
                }}>
                    {[
                        {
                            id: 'all',
                            label: 'Total Verified',
                            subtitle: 'All in database',
                            count: reachStats?.total_candidates ?? candidates.length,
                            color: '#0F172A',
                            activeBg: '#F1F5F9',
                            activeBorder: '#334155'
                        },
                        {
                            id: 'invite_ready',
                            label: 'Ready for Invite',
                            subtitle: 'Seq 0 (Approved)',
                            count: reachStats?.invite_ready_count ?? 0,
                            color: '#4F46E5',
                            activeBg: '#EEF2FF',
                            activeBorder: '#6366F1'
                        },
                        {
                            id: 'verified_real',
                            label: 'Verified Real',
                            subtitle: 'Delivered inboxes',
                            count: reachStats?.verified_real_count ?? 0,
                            color: '#15803D',
                            activeBg: '#ECFDF5',
                            activeBorder: '#10B981'
                        },
                        {
                            id: 'opened_clicked',
                            label: 'Opened / Clicked',
                            subtitle: 'Engaged with email',
                            count: reachStats?.opened_clicked_count ?? 0,
                            color: '#0284C7',
                            activeBg: '#F0F9FF',
                            activeBorder: '#0EA5E9'
                        },
                        {
                            id: 'followup_1_ready',
                            label: 'Follow-up #1 Ready',
                            subtitle: 'Seq 1 (Wait 1w)',
                            count: reachStats?.followup_1_ready_count ?? 0,
                            color: '#7C3AED',
                            activeBg: '#F5F3FF',
                            activeBorder: '#8B5CF6'
                        },
                        {
                            id: 'followup_2_ready',
                            label: 'Follow-up #2 Ready',
                            subtitle: 'Seq 2 (Wait 1w)',
                            count: reachStats?.followup_2_ready_count ?? 0,
                            color: '#D97706',
                            activeBg: '#FFFBEB',
                            activeBorder: '#F59E0B'
                        },
                        {
                            id: 'followup_3_ready',
                            label: 'Follow-up #3 Ready',
                            subtitle: 'Seq 3 (Wait 1m)',
                            count: reachStats?.followup_3_ready_count ?? 0,
                            color: '#EA580C',
                            activeBg: '#FFF7ED',
                            activeBorder: '#F97316'
                        },
                        {
                            id: 'followup_4_ready',
                            label: 'Follow-up #4 Ready',
                            subtitle: 'Seq 4 (Wait 1m)',
                            count: reachStats?.followup_4_ready_count ?? 0,
                            color: '#DC2626',
                            activeBg: '#FEF2F2',
                            activeBorder: '#EF4444'
                        },
                        {
                            id: 'registered',
                            label: 'Registered',
                            subtitle: 'Onboarded Owners',
                            count: reachStats?.registered_count ?? 0,
                            color: '#166534',
                            activeBg: '#DCFCE7',
                            activeBorder: '#16A34A',
                            hasCheck: true
                        },
                        {
                            id: 'bounced',
                            label: 'Bounced / Invalid',
                            subtitle: 'Safely isolated',
                            count: reachStats?.bounced_count ?? 0,
                            color: '#DC2626',
                            activeBg: '#FEF2F2',
                            activeBorder: '#EF4444'
                        }
                    ].map(card => {
                        const isSelected = reachFilter === card.id;
                        return (
                            <Box
                                key={card.id}
                                onClick={() => {
                                    if (card.id === 'all') {
                                        setReachFilter('all');
                                    } else {
                                        setReachFilter(isSelected ? 'all' : card.id);
                                    }
                                }}
                                sx={{
                                    p: isMobile ? 0.85 : 1.15,
                                    bgcolor: isSelected ? card.activeBg : '#F8FAFC',
                                    borderRadius: isMobile ? '8px' : '10px',
                                    border: isSelected ? `2px solid ${card.activeBorder}` : '1px solid #E2E8F0',
                                    cursor: 'pointer',
                                    transition: 'all 0.18s ease-in-out',
                                    position: 'relative',
                                    boxShadow: isSelected 
                                        ? `0 3px 10px rgba(0, 0, 0, 0.08)` 
                                        : '0 1px 2px rgba(0, 0, 0, 0.03)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    userSelect: 'none',
                                    '&:hover': {
                                        borderColor: card.activeBorder,
                                        bgcolor: isSelected ? card.activeBg : '#FFFFFF',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.06)'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
                                        {card.hasCheck && (
                                            <CheckCircleIcon sx={{ fontSize: isMobile ? 12 : 13, color: '#166534', flexShrink: 0 }} />
                                        )}
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                color: isSelected ? card.color : '#475569', 
                                                fontWeight: isSelected ? 700 : 600, 
                                                fontSize: isMobile ? '0.67rem' : '0.72rem', 
                                                lineHeight: 1.2,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            {card.label}
                                        </Typography>
                                    </Box>
                                    {isSelected && (
                                        <Box 
                                            sx={{ 
                                                width: 6, 
                                                height: 6, 
                                                borderRadius: '50%', 
                                                bgcolor: card.activeBorder,
                                                flexShrink: 0
                                            }} 
                                        />
                                    )}
                                </Box>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        fontWeight: 800, 
                                        color: card.count > 0 ? card.color : '#94A3B8', 
                                        fontSize: isMobile ? '1.05rem' : '1.25rem', 
                                        my: isMobile ? 0.15 : 0.2, 
                                        lineHeight: 1.15 
                                    }}
                                >
                                    {card.count}
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: isSelected ? card.color : '#94A3B8', 
                                        fontSize: isMobile ? '0.62rem' : '0.66rem', 
                                        lineHeight: 1.1,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                    {card.subtitle}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Paper>
            <Box sx={{ 
                mb: isMobile ? 1 : 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: isMobile ? 'wrap' : 'nowrap',
                px: isMobile ? 1 : 0
            }}>
                <Typography 
                    variant={isMobile ? "h6" : "h5"} 
                    sx={{
                        flex: isMobile ? '1 1 auto' : 'none',
                        fontSize: isMobile ? '1.1rem' : undefined
                    }}
                >
                    Owner Candidates
                </Typography>
                <TextField
                    size="small"
                    placeholder="Search name, industry, code..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ 
                        minWidth: isMobile ? '100%' : 220,
                        flex: isMobile ? '1 1 100%' : 'none'
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#94A3B8', fontSize: 19 }} />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchQuery('')}>
                                    <CancelIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </InputAdornment>
                        ) : null
                    }}
                />
                <FormControl 
                    size="small" 
                    sx={{ 
                        minWidth: isMobile ? '100%' : 130,
                        flex: isMobile ? '1 1 100%' : 'none'
                    }}
                >
                    <InputLabel id="type-filter-label">Type</InputLabel>
                    <Select
                        labelId="type-filter-label"
                        value={typeFilter}
                        label="Type"
                        onChange={(e) => {
                            setTypeFilter(e.target.value);
                            localStorage.setItem('candidatesTypeFilter', e.target.value);
                        }}
                    >
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="agency">Agencies Only</MenuItem>
                        <MenuItem value="business">Businesses Only</MenuItem>
                    </Select>
                </FormControl>
                <FormControl 
                    size="small" 
                    sx={{ 
                        minWidth: isMobile ? '100%' : 120,
                        flex: isMobile ? '1 1 100%' : 'none'
                    }}
                >
                    <InputLabel id="location-filter-label">Location</InputLabel>
                    <Select
                        labelId="location-filter-label"
                        value={locationFilter}
                        label="Location"
                        onChange={handleLocationFilterChange}
                    >
                        {getUniqueLocations().map(loc => (
                            <MenuItem key={loc.value} value={loc.value}>
                                {loc.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl 
                    size="small" 
                    sx={{ 
                        minWidth: isMobile ? '100%' : 120,
                        flex: isMobile ? '1 1 100%' : 'none'
                    }}
                >
                    <InputLabel id="status-filter-label">Status</InputLabel>
                    <Select
                        labelId="status-filter-label"
                        value={statusFilter}
                        label="Status"
                        onChange={handleStatusFilterChange}
                    >
                        {getUniqueStatuses().map(status => (
                            <MenuItem key={status.value} value={status.value}>
                                {status.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Optional BCC to register@mytowncode.com Toggle */}
                <Tooltip title="Optional test mode: When ON, sends a blind copy of every processed email to register@mytowncode.com">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={bccEnabled}
                                onChange={(e) => handleBccToggle(e.target.checked)}
                                color="secondary"
                                size="small"
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        fontWeight: 600, 
                                        fontSize: '0.82rem', 
                                        color: bccEnabled ? 'secondary.main' : 'text.secondary', 
                                        whiteSpace: 'nowrap' 
                                    }}
                                >
                                    BCC register@mytowncode.com
                                </Typography>
                                <Chip 
                                    label={bccEnabled ? "ON" : "OFF"} 
                                    size="small" 
                                    color={bccEnabled ? "secondary" : "default"} 
                                    variant={bccEnabled ? "filled" : "outlined"}
                                    sx={{ height: 18, fontSize: '0.62rem', fontWeight: 800 }} 
                                />
                            </Box>
                        }
                        sx={{
                            display: isMobile ? 'none' : 'inline-flex',
                            ml: isMobile ? 0 : 1,
                            mr: isMobile ? 0 : 'auto',
                            px: 1.5,
                            py: 0.35,
                            borderRadius: '10px',
                            bgcolor: bccEnabled ? '#F5F3FF' : '#F8FAFC',
                            border: '1px solid',
                            borderColor: bccEnabled ? '#DDD6FE' : '#E2E8F0',
                            transition: 'all 0.2s ease',
                            userSelect: 'none'
                        }}
                    />
                </Tooltip>

                <Box sx={{ ml: isMobile ? 0 : 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={() => {
                            setScoutMessage(null);
                            setScoutDialogOpen(true);
                        }}
                        sx={{
                            whiteSpace: 'nowrap',
                            fontWeight: 700,
                            borderRadius: '10px',
                            boxShadow: '0 4px 14px rgba(79, 70, 229, 0.28)'
                        }}
                    >
                        Launch AI Scout
                    </Button>
                </Box>
            </Box>

            {isMobile ? (
                <Box sx={{ 
                    flexGrow: 1, 
                    overflow: 'auto',
                    px: 1
                }}>
                    {renderMobileView()}
                </Box>
            ) : (
                <TableContainer 
                    component={Paper} 
                    sx={{ 
                        flexGrow: 1,
                        height: '100%',
                        overflow: 'auto'
                    }}
                >
                    <Table 
                        size="small" 
                        stickyHeader
                        sx={{
                            '& .MuiTableCell-root': {
                                py: 1,
                                px: 1.5,
                                fontSize: '0.875rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            },
                            '& .MuiTableCell-head': {
                                backgroundColor: theme.palette.background.paper,
                                fontWeight: 'bold'
                            },
                            tableLayout: 'fixed',
                            width: '100%'
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '105px' }}>Created</TableCell>
                                <TableCell sx={{ width: '70px' }}>Code</TableCell>
                                <TableCell sx={{ width: '190px' }}>Company</TableCell>
                                <TableCell sx={{ width: '190px' }}>Contact</TableCell>
                                <TableCell sx={{ width: '70px' }}>Location</TableCell>
                                <TableCell sx={{ width: '205px' }}>Status & Outreach</TableCell>
                                <TableCell>Notes</TableCell>
                                <TableCell sx={{ width: '130px' }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.isArray(getFilteredCandidates()) && getFilteredCandidates().map((candidate) => (
                                <TableRow 
                                    key={candidate.id}
                                    hover
                                    sx={{
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        bgcolor: candidate.registration_completed ? '#F0FDF4' : 'inherit',
                                        borderLeft: candidate.registration_completed ? '4px solid #16A34A' : undefined
                                    }}
                                >
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>
                                            {formatDdMmmYy(candidate.created) || new Date(candidate.created).toLocaleDateString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const modalLogo = candidate.code_image_url || 
                                                (candidate.company_website ? `https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${encodeURIComponent(candidate.company_website)}&size=128` : null);
                                            return (
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: 0.5
                                                }}>
                                                    <Avatar
                                                        src={modalLogo}
                                                        alt={`${candidate.company_name} logo`}
                                                        variant="rounded"
                                                        sx={{ 
                                                            width: 36, 
                                                            height: 36,
                                                            bgcolor: '#F1F5F9',
                                                            border: '1px solid #E2E8F0',
                                                            fontSize: '0.8rem',
                                                            fontWeight: 700,
                                                            color: '#475569'
                                                        }}
                                                    >
                                                        {candidate.company_name ? candidate.company_name.charAt(0).toUpperCase() : '🏢'}
                                                    </Avatar>
                                                    {candidate.earliest_code_value && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontSize: '0.65rem',
                                                                color: 'text.secondary'
                                                            }}
                                                        >
                                                            {candidate.earliest_code_value}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        fontWeight: 500,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                >
                                                    {candidate.company_name}
                                                </Typography>
                                                {candidate.registration_completed && (
                                                    <Tooltip title="Registered Code Owner (Completed Onboarding)">
                                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#16A34A', flexShrink: 0 }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, my: 0.25 }}>
                                                <Tooltip title={candidate.is_agency ? "Ad / Media Agency. Click to toggle." : "Regular Business. Click to toggle."}>
                                                    <Chip 
                                                        label={candidate.is_agency ? "Agency" : "Business"} 
                                                        size="small" 
                                                        color={candidate.is_agency ? "secondary" : "default"}
                                                        variant={candidate.is_agency ? "filled" : "outlined"}
                                                        onClick={() => handleToggleAgency(candidate.id, candidate.is_agency)}
                                                        sx={{ 
                                                            height: 18, 
                                                            fontSize: '0.62rem', 
                                                            fontWeight: 600,
                                                            cursor: 'pointer'
                                                        }} 
                                                    />
                                                </Tooltip>
                                                {candidate.industry && (
                                                    <Typography 
                                                        variant="caption" 
                                                        color="textSecondary"
                                                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                    >
                                                        {candidate.industry}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Typography 
                                                variant="caption" 
                                                color="primary"
                                                onClick={() => handleUrlClick(candidate.company_website)}
                                                sx={{ 
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                    fontSize: '0.75rem',
                                                    lineHeight: 1.2,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    '&:hover': {
                                                        color: 'primary.dark'
                                                    }
                                                }}
                                            >
                                                {candidate.company_website}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Typography 
                                                variant="body2"
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {candidate.company_email}
                                            </Typography>
                                            <Typography 
                                                variant="caption" 
                                                color="textSecondary"
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {candidate.company_business}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{candidate.city_code}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                            {getStatusChip(candidate)}
                                            {renderEmailReachChip(candidate)}
                                            {renderEmailDates(candidate)}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography 
                                            variant="body2"
                                            color={theme => {
                                                const content = getNotesContent(candidate);
                                                if (content === 'invalid_email' || content.includes('failed') || candidate.email_reach_status === 'bounced_invalid') {
                                                    return theme.palette.error.main;
                                                }
                                                return theme.palette.text.secondary;
                                            }}
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {getNotesContent(candidate)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ 
                                            display: 'flex', 
                                            gap: 0.5, 
                                            justifyContent: 'flex-end',
                                            flexWrap: 'nowrap',
                                            '& .MuiIconButton-root': {
                                                width: 40,
                                                height: 40
                                            }
                                        }}>
                                            {shouldShowActionIcons(candidate).approving && (
                                                <Tooltip title="Approving candidate...">
                                                    <IconButton size="small" disabled sx={{ width: 40, height: 40 }}>
                                                        <CircularProgress size={20} color="primary" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {shouldShowActionIcons(candidate).approve && (
                                                <Tooltip title="Approve">
                                                    <IconButton 
                                                        onClick={() => handleApprove(candidate.id)}
                                                        disabled={loading || Boolean(approvingCandidates[candidate.id])}
                                                        color="primary"
                                                        size="small"
                                                    >
                                                        <CheckCircleIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {shouldShowActionIcons(candidate).email && (
                                                <Tooltip title={getEmailStatusTooltip(candidate.id)}>
                                                    <IconButton 
                                                        onClick={() => handleOpenEmailDialog(candidate)}
                                                        disabled={sendingEmails[candidate.id] || loading}
                                                        color={candidate.email_sequence > 0 ? "secondary" : "primary"}
                                                        size="small"
                                                    >
                                                        {candidate.email_sequence > 0 ? <ForwardToInboxIcon /> : <EmailIcon />}
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {shouldShowActionIcons(candidate).progress && getEmailStatusIcon(candidate.id)}
                                            {shouldShowActionIcons(candidate).bounced && getEmailStatusIcon(candidate.id)}
                                            {shouldShowActionIcons(candidate).blacklist && (
                                                <Tooltip title="Blacklist">
                                                    <IconButton 
                                                        onClick={() => setBlacklistDialog({
                                                            open: true,
                                                            candidateId: candidate.id,
                                                            candidateName: candidate.company_name
                                                        })}
                                                        color="error"
                                                        size="small"
                                                    >
                                                        <BlockIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {shouldShowActionIcons(candidate).delete && (
                                                <Tooltip title="Delete">
                                                    <IconButton 
                                                        onClick={() => setDeleteDialog({
                                                            open: true,
                                                            candidateId: candidate.id,
                                                            candidateName: candidate.company_name
                                                        })}
                                                        color="error"
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Blacklist Dialog */}
            <Dialog 
                open={blacklistDialog.open} 
                onClose={() => setBlacklistDialog({ open: false, candidateId: null, candidateName: '' })}
            >
                <DialogTitle>Blacklist Candidate</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to blacklist <strong>{blacklistDialog.candidateName || 'this candidate'}</strong>? This will permanently prevent outreach and ensure Scout AI never suggests them again.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setBlacklistDialog({ open: false, candidateId: null, candidateName: '' })}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleBlacklist} 
                        color="error" 
                        variant="contained"
                    >
                        Blacklist
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog 
                open={deleteDialog.open} 
                onClose={() => setDeleteDialog({ open: false, candidateId: null, candidateName: '' })}
            >
                <DialogTitle>Delete Candidate</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the candidate "{deleteDialog.candidateName}"? 
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setDeleteDialog({ 
                            open: false, 
                            candidateId: null, 
                            candidateName: '' 
                        })}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDelete} 
                        color="error" 
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* In-House AI Scout Dialog */}
            <Dialog 
                open={scoutDialogOpen} 
                onClose={() => !scoutLoading && setScoutDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
                    <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', width: 36, height: 36 }}>
                        <AutoAwesomeIcon fontSize="small" />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            In-House Scouting Agent
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Discover prospective B2C Code Owner candidates via OpenAI
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ pt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                        The Scout Agent analyzes target B2C offline advertisers (billboards, public transport, radio), validates websites and direct contact emails, checks exclusions against the database, and registers them directly into PostgreSQL for your approval.
                    </Typography>

                    {scoutMessage && (
                        <Alert 
                            severity={scoutMessage.type} 
                            sx={{ mb: 2.5, borderRadius: 2 }}
                            onClose={() => setScoutMessage(null)}
                        >
                            {scoutMessage.text}
                        </Alert>
                    )}

                    <Stack spacing={2.5}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="scout-city-label">Target City</InputLabel>
                            <Select
                                labelId="scout-city-label"
                                value={scoutCity}
                                label="Target City"
                                onChange={(e) => setScoutCity(e.target.value)}
                                disabled={scoutLoading}
                            >
                                <MenuItem value=""><em>-- Select an available city --</em></MenuItem>
                                {cityInfo.map(city => {
                                    const code = city.code_3 || city.code_2;
                                    return (
                                        <MenuItem key={city.id || code} value={code}>
                                            {city.city_name} ({code}) - {city.country_name} {city.search_on ? '🟢' : ''}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            size="small"
                            label="Or Custom City Code / Name"
                            placeholder="e.g. SIN, NYC, LON, AMS, DUB"
                            value={scoutCity}
                            onChange={(e) => setScoutCity(e.target.value.toUpperCase())}
                            disabled={scoutLoading}
                            helperText="Uses 2- or 3-letter city code or city name"
                        />

                        <FormControl fullWidth size="small">
                            <InputLabel id="scout-count-label">Candidates to Discover</InputLabel>
                            <Select
                                labelId="scout-count-label"
                                value={scoutCount}
                                label="Candidates to Discover"
                                onChange={(e) => setScoutCount(Number(e.target.value))}
                                disabled={scoutLoading}
                            >
                                <MenuItem value={5}>5 candidates (Quick test)</MenuItem>
                                <MenuItem value={10}>10 candidates (Standard batch)</MenuItem>
                                <MenuItem value={15}>15 candidates (Deep search)</MenuItem>
                                <MenuItem value={20}>20 candidates (Maximum batch)</MenuItem>
                            </Select>
                        </FormControl>

                        {scoutLoading && (
                            <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <CircularProgress size={24} color="primary" />
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        AI Scout Agent is researching candidates...
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Querying OpenAI, validating contact emails, and filtering database duplicates.
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button 
                        onClick={() => setScoutDialogOpen(false)} 
                        disabled={scoutLoading}
                        color="inherit"
                    >
                        Close
                    </Button>
                    <Button 
                        onClick={handleRunScout} 
                        color="primary" 
                        variant="contained"
                        disabled={scoutLoading || !scoutCity}
                        startIcon={scoutLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                        sx={{ px: 2.5 }}
                    >
                        {scoutLoading ? 'Scouting...' : 'Start Scouting'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Email Dispatch & Follow-up Dialog */}
            <Dialog 
                open={emailDispatchDialog.open} 
                onClose={() => !sendingEmails[emailDispatchDialog.candidate?.id] && setEmailDispatchDialog(prev => ({ ...prev, open: false }))}
                maxWidth="sm" 
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {emailDispatchDialog.candidate?.email_sequence === 0 
                            ? 'Send Initial Welcome Invitation' 
                            : `Send Follow-up #${(emailDispatchDialog.candidate?.email_sequence || 0) + 1}`}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                        Sender: Konstantin Ryabov &lt;konstantin.ryabov@mytowncode.com&gt;
                    </Typography>
                </DialogTitle>
                <DialogContent dividers sx={{ pt: 2 }}>
                    {emailDispatchDialog.candidate && (
                        <Stack spacing={2.5}>
                            {/* Candidate details summary box */}
                            <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                                    {emailDispatchDialog.candidate.company_name}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#4F46E5', fontWeight: 600 }}>
                                    {emailDispatchDialog.candidate.company_email}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                    <Chip 
                                        label={`Location: ${emailDispatchDialog.candidate.city_code}`} 
                                        size="small" 
                                        variant="outlined" 
                                        sx={{ height: 20, fontSize: '0.65rem' }} 
                                    />
                                    <Chip 
                                        label={emailDispatchDialog.candidate.is_agency ? "Ad / Media Agency" : "B2C Business"} 
                                        size="small" 
                                        color={emailDispatchDialog.candidate.is_agency ? "secondary" : "default"}
                                        sx={{ height: 20, fontSize: '0.65rem' }} 
                                    />
                                    <Chip 
                                        label={`Sequence Step: ${(emailDispatchDialog.candidate.email_sequence || 0) + 1} of 3`} 
                                        size="small" 
                                        variant="outlined" 
                                        sx={{ height: 20, fontSize: '0.65rem' }} 
                                    />
                                </Box>
                            </Box>

                            {/* Verification Signal Banner */}
                            {['verified_real', 'opened', 'clicked'].includes(emailDispatchDialog.candidate.email_reach_status) ? (
                                <Alert severity="success" sx={{ borderRadius: '10px' }}>
                                    <strong>Verified Real Mailbox!</strong> Previous message was successfully delivered without bounce. Safe for follow-up outreach.
                                </Alert>
                            ) : (
                                <Alert severity="info" sx={{ borderRadius: '10px' }}>
                                    <strong>Initial Outreach Touchpoint:</strong> Sending invitation from <code>konstantin.ryabov@mytowncode.com</code> with direct reply path.
                                </Alert>
                            )}

                            {/* Cadence Check Notice */}
                            {(() => {
                                if (!emailDispatchDialog.candidate.email_sequence || emailDispatchDialog.candidate.email_sequence === 0) return null;
                                const sentTime = emailDispatchDialog.candidate.last_email_sent_at || emailDispatchDialog.candidate.initial_email_sent_at;
                                if (!sentTime) return null;
                                const hoursSince = (Date.now() - new Date(sentTime).getTime()) / (1000 * 60 * 60);
                                const isEngaged = ['opened', 'clicked'].includes(emailDispatchDialog.candidate.email_reach_status);
                                const minHours = isEngaged ? 48 : 72;
                                if (hoursSince < minHours) {
                                    const hoursLeft = Math.ceil(minHours - hoursSince);
                                    const daysLeft = Math.ceil(hoursLeft / 24);
                                    return (
                                        <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                                            <strong>Cadence Notice:</strong> Previous email was sent only {Math.round(hoursSince)}h ago ({formatDdMmmYy(sentTime)}). Recommended reminder interval is 2–3 days (48–72h) after initial invite (~{daysLeft} day{daysLeft > 1 ? 's' : ''} remaining).
                                        </Alert>
                                    );
                                }
                                return null;
                            })()}

                            {/* Template Selector */}
                            <FormControl fullWidth size="small">
                                <InputLabel id="template-select-label">Email Template</InputLabel>
                                <Select
                                    labelId="template-select-label"
                                    value={emailDispatchDialog.templateId}
                                    label="Email Template"
                                    onChange={(e) => setEmailDispatchDialog(prev => ({ ...prev, templateId: Number(e.target.value) }))}
                                >
                                    <MenuItem value={adminApi.EMAIL_TEMPLATES.WELCOME}>
                                        Template 1: Welcome Invitation (B2C Merchant)
                                    </MenuItem>
                                    <MenuItem value={adminApi.EMAIL_TEMPLATES.AGENCY_PITCH}>
                                        Template 2: Agency Pitch (Advertising & Media Agency)
                                    </MenuItem>
                                    <MenuItem value={adminApi.EMAIL_TEMPLATES.REMINDER}>
                                        Template 3: Follow-up Reminder #1 (B2C Gentle Follow-up)
                                    </MenuItem>
                                    <MenuItem value={adminApi.EMAIL_TEMPLATES.REMINDER_AGENCY}>
                                        Template 4: Follow-up Reminder #1 (Agency Pitch Follow-up)
                                    </MenuItem>
                                    <MenuItem value={adminApi.EMAIL_TEMPLATES.FINAL_CHECK}>
                                        Template 5: Final Check #2 (B2C Merchant)
                                    </MenuItem>
                                    <MenuItem value={adminApi.EMAIL_TEMPLATES.FINAL_CHECK_AGENCY}>
                                        Template 6: Final Check #2 (Agency Pitch Final)
                                    </MenuItem>
                                </Select>
                            </FormControl>

                            <Box sx={{ p: 1.5, bgcolor: '#EEF2FF', borderRadius: '8px', border: '1px solid #C7D2FE' }}>
                                <Typography variant="caption" sx={{ color: '#3730A3', display: 'block', fontWeight: 600 }}>
                                    ✓ Direct SMTP via SendGrid with verified DKIM & SPF on mytowncode.com
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#4338CA', display: 'block', mt: 0.5 }}>
                                    ✓ Webhook tracking monitors Delivered, Open, and Click in real-time
                                </Typography>
                                <Typography variant="caption" sx={{ color: bccEnabled ? '#B45309' : '#15803D', display: 'block', mt: 0.5, fontWeight: 700 }}>
                                    {bccEnabled 
                                        ? '⚠️ BCC Active: A copy will also be delivered to register@mytowncode.com (Global toggle is ON)' 
                                        : '✓ BCC Disabled: Standard clean outreach to recipient only'}
                                </Typography>
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button 
                        onClick={() => setEmailDispatchDialog(prev => ({ ...prev, open: false }))} 
                        color="inherit"
                        disabled={sendingEmails[emailDispatchDialog.candidate?.id]}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmSendEmail} 
                        color="primary" 
                        variant="contained"
                        disabled={sendingEmails[emailDispatchDialog.candidate?.id]}
                        startIcon={sendingEmails[emailDispatchDialog.candidate?.id] ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                        sx={{ fontWeight: 700, borderRadius: '8px', px: 2.5 }}
                    >
                        {sendingEmails[emailDispatchDialog.candidate?.id] ? 'Sending...' : 'Send Email Now'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CandidatesManager; 